import cn from 'classnames';
import {
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import {Close, OpenInNew} from '@material-ui/icons';
import {FormattedMessage, useIntl} from 'react-intl';
import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {StudioSelect} from '../../../base-components/StudioSelect';
import {DeviceGroupNode} from '../../../types/deployment/DeploymentDeviceGroup';
import URL from '../../../config/url';
import {StudioCheckbox} from '../../../base-components/StudioCheckbox';
import axios from 'axios';
import {useDeploy} from '../../TestDeployView/useDeploy';
import FormButton from '../../../base-components/StudioButton/FormButton';
import {StartUponDeploymentNodeType} from './CreateDeployment';
import {InputChoice} from '../DeploymentDetails/DeploymentDetails';
import {StudioTooltip} from '../../../base-components/StudioTooltip';

import './CreateDeployment.scss';
import Waiting from '../../../base-components/StudioWaiting/Waiting';

type TargetDeviceGroupTableType = {
  deviceGroupId: string;
  deviceGroupName: string;
  isTargetDeviceGroupTableModalOpen: boolean;
  setIsTargetDeviceGroupTableModalOpen: (value: boolean) => void;
  isDeploying: boolean;
  startUponDeploymentNodes: StartUponDeploymentNodeType[];
  setStartUponDeploymentNodes: (data: StartUponDeploymentNodeType[]) => void;
  showCheckBoxes: boolean;
  show: boolean;
};

type DeviceGroupNodeRow = DeviceGroupNode & {selected: boolean};

const InputSourceTypeOptions: Record<InputChoice, string> = {
  DEVICE: 'On Device',
  EXTERNAL: 'External',
};

const ExternalSourceTypeOtions = {
  FILE: 'Video File',
  IP_CAMERA: 'Video Source',
};

const TargetDeviceGroupTable = ({
  deviceGroupId,
  deviceGroupName,
  isTargetDeviceGroupTableModalOpen,
  setIsTargetDeviceGroupTableModalOpen,
  isDeploying,
  startUponDeploymentNodes,
  setStartUponDeploymentNodes,
  show,
  showCheckBoxes,
}: TargetDeviceGroupTableType) => {
  const [{streamingDevices}] = useDeploy();
  const intl = useIntl();
  const [nodes, setNodes] = useState<DeviceGroupNodeRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (deviceGroupId) {
      setLoading(true);
      getNodes()
        .then(res => {
          setStartUponDeploymentNodes([]);
          const newNodes = res?.nodes?.map((n: DeviceGroupNode) => ({
            ...n,
            selected: false,
          }));
          setNodes(newNodes || []);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceGroupId]);

  async function getNodes() {
    const {data} = await axios.get(URL.DEVICE_GROUP_NODES, {
      params: {groupId: deviceGroupId},
    });
    return data.body;
  }
  const startUponDeploymentIds = useMemo(
    () => startUponDeploymentNodes.map(n => n.nodeId),
    [startUponDeploymentNodes]
  );

  const handleSelectNode = useCallback(
    (id: string) => {
      if (startUponDeploymentIds.includes(id)) {
        setStartUponDeploymentNodes(
          startUponDeploymentNodes.filter(n => n.nodeId !== id)
        );
      } else {
        const node = nodes.find(n => n.id === id);
        node &&
          setStartUponDeploymentNodes([
            ...startUponDeploymentNodes,
            {
              nodeId: node.id,
              deploymentId: '',
              deployStatus: '',
              inputSourceType: null,
              inputSource: '',
              externalSourceType: null,
            },
          ]);
      }
    },
    [startUponDeploymentIds, setStartUponDeploymentNodes, startUponDeploymentNodes, nodes]
  );

  const handleNodeSource = useCallback(
    (nodeId: string, data: Partial<StartUponDeploymentNodeType>) => {
      const updatedSelectedNodes = startUponDeploymentNodes.map(n => {
        if (n.nodeId === nodeId) {
          return {
            ...n,
            ...data,
          };
        } else {
          return n;
        }
      });
      setStartUponDeploymentNodes(updatedSelectedNodes);
    },
    [startUponDeploymentNodes, setStartUponDeploymentNodes]
  );

  const closeIsTargetDeviceGroupTableModal = useCallback(() => {
    setIsTargetDeviceGroupTableModalOpen(false);
  }, [setIsTargetDeviceGroupTableModalOpen]);

  const AssignStreamingDevice = ({row}: {row: DeviceGroupNodeRow}) => {
    const node = startUponDeploymentNodes.find(n => n.nodeId === row.id);
    const [fieldsTouched, setFiledsTouched] = useState({
      inputSource: false,
      inputSourceType: false,
      externalSourceType: false,
    });
    const onDeviceSourceOptions = useMemo(() => {
      const rowSources = nodes.find(n => n.id === row.id)?.sources || [];
      return rowSources.reduce<Record<string, string>>((acc, node) => {
        if (node) {
          acc[node.sourceId] = node.sourceName;
        }
        return acc;
      }, {});
    }, [nodes, row]);
    const externalDeviceOptions = useMemo(() => {
      return streamingDevices.reduce<Record<string, string>>((options, newOption) => {
        if (newOption.type === node?.externalSourceType) {
          options[newOption.id] = newOption.name;
        }

        return options;
      }, {});
    }, [streamingDevices]);

    if (!node) return <></>;

    const inputSourceOptions =
      node.inputSourceType === 'DEVICE' ? onDeviceSourceOptions : externalDeviceOptions;

    return (
      <>
        <TableCell className="create-prod-deploy__cell">
          <StudioSelect
            className={cn(
              'create-prod-deploy__field',
              'create-prod-deploy__field__stream',
              node.inputSourceType
                ? 'create-prod-deploy__select__selected'
                : 'create-prod-deploy__select__unselected'
            )}
            dataTestId="create-prod-deploy-source-type"
            placeholder={intl.formatMessage({
              id: 'createDeploy.selectSource',
            })}
            disablePlaceholder
            options={InputSourceTypeOptions}
            SelectProps={{
              value: node.inputSourceType === null ? '' : node.inputSourceType,
              onChange: event => {
                if (node.inputSourceType !== event.target.value) {
                  handleNodeSource(row.id, {
                    inputSourceType: event.target.value as InputChoice,
                    externalSourceType: null,
                    inputSource: '',
                  });
                  setFiledsTouched({
                    ...fieldsTouched,
                    inputSourceType: true,
                    externalSourceType: false,
                    inputSource: false,
                  });
                }
              },
            }}
            error={Boolean(fieldsTouched.inputSourceType)}
            helperText={
              Boolean(fieldsTouched.inputSourceType) && !node.inputSourceType?.length
                ? 'Required'
                : ''
            }
            disabled={isDeploying}
            required
          />
        </TableCell>
        <TableCell className="create-prod-deploy__cell">
          {node.inputSourceType === 'EXTERNAL' && (
            <StudioSelect
              className={cn(
                'create-prod-deploy__field',
                'create-prod-deploy__field__stream',
                node.externalSourceType
                  ? 'create-prod-deploy__select__selected'
                  : 'create-prod-deploy__select__unselected'
              )}
              placeholder={intl.formatMessage({
                id: 'createDeploy.selectSource',
              })}
              dataTestId="create-prod-deploy-configure-external-source"
              options={ExternalSourceTypeOtions}
              disablePlaceholder
              SelectProps={{
                value: node.externalSourceType,
                onChange: (e: React.ChangeEvent<any>) => {
                  handleNodeSource(row.id, {externalSourceType: e.target.value});
                },
                onClick: () =>
                  setFiledsTouched({...fieldsTouched, externalSourceType: true}),
              }}
              error={Boolean(fieldsTouched.externalSourceType)}
              helperText={
                Boolean(fieldsTouched.externalSourceType) &&
                !node.externalSourceType?.length
                  ? 'Required'
                  : ''
              }
              disabled={isDeploying}
              required
            />
          )}
        </TableCell>
        <TableCell className="create-prod-deploy__cell">
          {Boolean(
            node.inputSourceType === 'DEVICE' ||
              (node.inputSourceType === 'EXTERNAL' &&
                (node.externalSourceType === 'IP_CAMERA' ||
                  node.externalSourceType === 'FILE'))
          ) ? (
            <>
              <StudioSelect
                className={cn(
                  'create-prod-deploy__field',
                  'create-prod-deploy__field__stream',
                  node.inputSource
                    ? 'create-prod-deploy__select__selected'
                    : 'create-prod-deploy__select__unselected'
                )}
                placeholder="Select a source"
                dataTestId="create-prod-deploy-configure-input-source"
                options={inputSourceOptions}
                disablePlaceholder
                SelectProps={{
                  value: node.inputSource,
                  onChange: (e: React.ChangeEvent<any>) => {
                    handleNodeSource(row.id, {inputSource: e.target.value});
                  },
                  onClick: () => setFiledsTouched({...fieldsTouched, inputSource: true}),
                }}
                error={Boolean(fieldsTouched.inputSource)}
                helperText={
                  Boolean(fieldsTouched.inputSource) && !node.inputSource.length
                    ? 'Required'
                    : ''
                }
                disabled={isDeploying}
                required
              />
            </>
          ) : null}
        </TableCell>
      </>
    );
  };

  const selectedRows = useMemo(() => nodes.filter(e => e.selected).map(e => e.id), [
    nodes,
  ]);
  const numberOfSelectedRows = useMemo(() => selectedRows.length, [selectedRows]);

  const toggleCheckAndUncheckAll = useCallback(() => {
    const newNodes = nodes.map(e => {
      return {
        ...e,
        selected: numberOfSelectedRows === nodes.length ? false : true,
      };
    });
    setNodes(newNodes);
  }, [nodes, setNodes, numberOfSelectedRows]);

  const RenderTable = ({data}: {data: DeviceGroupNodeRow[]}) => {
    return (
      <TableContainer
        component={Paper}
        className={cn(
          'prod-deploy-dashboard__deployments',
          'create-prod-deploy__devices__table'
        )}
        elevation={0}
      >
        <Table stickyHeader className="prod-deploy-dashboard__table">
          <TableHead>
            {numberOfSelectedRows > 0 && (
              <TableRow className="create-prod-deploy__group__select">
                <TableCell colSpan={4}>
                  <p>{`${numberOfSelectedRows} item${
                    numberOfSelectedRows > 1 ? 's' : ''
                  } selected`}</p>
                </TableCell>
                <TableCell colSpan={4}>
                  <StudioTooltip
                    title={intl.formatMessage({
                      id: 'prodDeployment.deviceGroupStartUponDeploy',
                    })}
                    arrow={false}
                    placement="top"
                  >
                    <Switch
                      checked={startUponDeploymentIds.length === nodes.length}
                      onChange={toggleSelectedRowsForStartUponDeployment}
                      name="startUponDeploy"
                      color="primary"
                      disabled={isDeploying}
                      className="create-prod-deploy__switch"
                    />
                  </StudioTooltip>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              {showCheckBoxes && (
                <TableCell>
                  <StudioCheckbox
                    checked={numberOfSelectedRows === data.length}
                    name="select"
                    onChange={toggleCheckAndUncheckAll}
                  />
                </TableCell>
              )}
              <TableCell>
                <FormattedMessage id="prodDeployment.devices" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.status" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.state" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.deviceGroupStartUponDeploy" />
              </TableCell>
              <TableCell colSpan={3}>
                <FormattedMessage id="prodDeployment.assignStreamingDevice" />
              </TableCell>
            </TableRow>

            <TableRow className="prod-deploy-dashboard__subheader">
              <TableCell colSpan={showCheckBoxes ? 5 : 4} />
              <TableCell className="create-prod-deploy__cell">
                <FormattedMessage id="createDeploy.inputSourceType" />
              </TableCell>
              <TableCell className="create-prod-deploy__cell">
                <FormattedMessage id="createDeploy.externalSourceType" />
              </TableCell>
              <TableCell className="create-prod-deploy__cell">
                <FormattedMessage id="createDeploy.deviceSource" tagName="span" />
                <span>&nbsp; / &nbsp;</span>
                <FormattedMessage id="createDeploy.externalSource" tagName="span" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={100} className="prod-deploy-dashboard__no-results">
                  <Waiting
                    diameter={40}
                    loadingText={intl.formatMessage({id: 'form.loading'})}
                    className="create-prod-deploy__loading"
                  />
                </TableCell>
              </TableRow>
            ) : data.length ? (
              <>
                {data.map(row => (
                  <RenderTableRow key={row.id} row={row} />
                ))}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={100} className="prod-deploy-dashboard__no-results">
                  <img src="images/emptyStateNoResults.png" alt="No deployment" />
                  <p>
                    <FormattedMessage id="prodDeployment.deviceGroupNoResult" />
                  </p>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell colSpan={100} className="prod-deploy-dashboard__no-results">
                <FormattedMessage
                  id="prodDeployment.deviceGroupResult"
                  values={{noOfResults: data.length}}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const toggleSelectedRowsForStartUponDeployment = () => {
    if (startUponDeploymentIds.length === nodes.length) {
      setStartUponDeploymentNodes([]);
    } else {
      const newNodesToStart = [];
      for (let i = 0; i < selectedRows.length; i++) {
        const node = selectedRows[i];
        if (!startUponDeploymentIds.includes(node)) {
          newNodesToStart.push({
            nodeId: node,
            inputSourceType: null,
            inputSource: '',
            externalSourceType: null,
            deploymentId: '',
            deployStatus: '',
          });
        }
      }
      setStartUponDeploymentNodes([...startUponDeploymentNodes, ...newNodesToStart]);
    }
  };

  const RenderTableRow = ({row}: {row: DeviceGroupNodeRow}) => {
    const rowStatus = startUponDeploymentNodes.find(node => node.nodeId === row.id)
      ?.deployStatus;

    return (
      <TableRow key={row.id}>
        {showCheckBoxes && (
          <TableCell>
            <StudioCheckbox
              checked={row.selected}
              name="check"
              onChange={(_, checked) => {
                const newNodes = nodes.map(e => {
                  return {
                    ...e,
                    selected: row.id === e.id ? checked : e.selected,
                  };
                });
                setNodes(newNodes);
              }}
            />
          </TableCell>
        )}
        <TableCell>
          <Typography>{row.name}</Typography>
        </TableCell>
        <TableCell>
          <span
            className={cn(
              'deploy-status__bulb',
              row.status === 'OFFLINE' && 'deploy-status__bulb--stopping'
            )}
          />
          <span className="create-prod-deploy__status_text">
            {row.status.charAt(0).toUpperCase() + row.status.slice(1).toLowerCase()}
          </span>
        </TableCell>
        <TableCell>
          <Typography>{rowStatus || '--'}</Typography>
        </TableCell>
        <TableCell>
          <Switch
            checked={startUponDeploymentIds.includes(row.id)}
            onChange={() => handleSelectNode(row.id)}
            name="startUponDeploy"
            color="primary"
            disabled={row.status === 'OFFLINE' || isDeploying}
            className="create-prod-deploy__switch"
          />
        </TableCell>
        {startUponDeploymentIds.includes(row.id) ? (
          <AssignStreamingDevice row={row} />
        ) : (
          <TableCell colSpan={3} />
        )}
      </TableRow>
    );
  };

  return (
    <Collapse in={show} timeout={300}>
      <div>
        <h4 className="create-prod-deploy__device_name">{deviceGroupName}</h4>
        <RenderTable data={nodes.slice(0, 3)} />
        <div
          className="create-prod-deploy__view_modal"
          onClick={() => {
            setIsTargetDeviceGroupTableModalOpen(true);
          }}
        >
          <span>
            {intl.formatMessage({
              id: 'dashboard.viewAll',
            })}
          </span>
          <span>
            <OpenInNew />
          </span>
        </div>
        <Dialog
          open={isTargetDeviceGroupTableModalOpen}
          className=""
          fullWidth
          maxWidth="xl"
        >
          <DialogTitle
            disableTypography={true}
            className="project-form-container__header"
          >
            <h4 className="project-form-container__header__title">
              <FormattedMessage id="prodDeployment.devices" />
            </h4>
            <span
              className="project-form-container__header__close"
              onClick={closeIsTargetDeviceGroupTableModal}
            >
              <Close />
            </span>
          </DialogTitle>
          <DialogContent className="add-streaming-device__form-body">
            <RenderTable data={nodes} />
          </DialogContent>
          <DialogActions className="add-streaming-device__actions">
            <FormButton
              type="button"
              buttonRole="secondary"
              value="Cancel"
              onClick={closeIsTargetDeviceGroupTableModal}
            />
            <FormButton
              type="button"
              buttonRole="primary"
              value="Save"
              onClick={closeIsTargetDeviceGroupTableModal}
            />
          </DialogActions>
        </Dialog>
      </div>
    </Collapse>
  );
};

export default TargetDeviceGroupTable;
